import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, MapPin, Store, ExternalLink } from "lucide-react";
import { toFullUrl } from "@/components/landing/utils";

export function locationMapsUrl(p) {
  return p.maps_url || `https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`;
}

export function LocationPhoto({ item, className = "" }) {
  const url = toFullUrl(item.image_url);
  if (url) {
    return <img src={url} alt={item.name} loading="lazy" decoding="async" className={`w-full h-full object-cover ${className}`} />;
  }
  return (
    <div className={`w-full h-full bg-[#18181B] flex items-center justify-center ${className}`}>
      <Store size={28} strokeWidth={1.2} className="text-[#FFBF57]/70" />
    </div>
  );
}

export default function LocationsCarousel({ items, selected, onSelect }) {
  const trackRef = useRef(null);
  const cardRefs = useRef([]);
  const mounted = useRef(false);

  useEffect(() => {
    const el = cardRefs.current[selected];
    const track = trackRef.current;
    if (!el || !track) return;
    const left = el.offsetLeft - (track.clientWidth - el.clientWidth) / 2;
    track.scrollTo({ left, behavior: mounted.current ? "smooth" : "auto" });
    mounted.current = true;
  }, [selected]);

  const step = (dir) => onSelect((selected + dir + items.length) % items.length);

  return (
    <div className="mt-6 relative" data-testid="locations-carousel">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-mono">
          Sucursal <span className="text-[#0A0A0A]">{selected + 1}</span> / {items.length}
        </div>
        <div className="flex gap-2">
          <button onClick={() => step(-1)} aria-label="Anterior" data-testid="carousel-prev" className="w-9 h-9 rounded-full border border-black/10 bg-white flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => step(1)} aria-label="Siguiente" data-testid="carousel-next" className="w-9 h-9 rounded-full border border-black/10 bg-white flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div ref={trackRef} className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2 -mx-5 px-5 sm:mx-0 sm:px-0">
        {items.map((p, i) => {
          const active = i === selected;
          return (
            <button
              key={`${p.name}-${i}`}
              ref={(el) => (cardRefs.current[i] = el)}
              onClick={() => onSelect(i)}
              data-testid={`location-card-${i}`}
              className={`snap-center shrink-0 w-[250px] sm:w-[280px] text-left rounded-2xl overflow-hidden border bg-white transition-all duration-300 ${active ? "border-[#FFBF57] shadow-[0_12px_40px_rgba(0,0,0,0.12)] scale-[1.01]" : "border-black/8 hover:border-black/25"}`}
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <LocationPhoto item={p} />
                <div className="absolute top-2.5 left-2.5 text-[9px] uppercase tracking-[0.18em] font-mono px-2 py-1 rounded-full bg-[#0A0A0A]/80 text-[#FFBF57] backdrop-blur">
                  Sucursal Rubí Perfumería
                </div>
                {p.badge && (
                  <div className="absolute bottom-2.5 left-2.5 right-2.5 text-[9px] uppercase tracking-[0.16em] font-mono px-2.5 py-1.5 rounded-lg bg-[#FFBF57] text-[#0A0A0A] font-semibold text-center" data-testid={`location-card-${i}-badge`}>
                    {p.badge}
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="text-sm font-semibold text-[#0A0A0A] truncate">{p.name}</div>
                <div className="text-[11px] text-[#52525B] mt-0.5 flex items-center gap-1.5">
                  <MapPin size={11} className="text-[#FFBF57] shrink-0" />
                  <span className="truncate">{p.city}{p.state ? ` · ${p.state}` : ""}</span>
                </div>
                {p.address && <div className="text-[11px] text-[#71717A] mt-2 leading-snug line-clamp-2">{p.address}</div>}
                <a
                  href={locationMapsUrl(p)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-[#0A0A0A] border-b border-[#FFBF57] pb-0.5 hover:text-[#B8862F]"
                  data-testid={`location-card-${i}-maps`}
                >
                  Ver ubicación <ExternalLink size={10} />
                </a>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
